import { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import AuthContext from '../context/AuthContext';
import Base from './Base';
import { Card, Button, Input, Textarea, Alert, Spinner } from './ui';


const ORANGE = '#d25614';
const ORANGE_HOVER = '#e67e0e';

const AboutMe = () => {
  const { user, authToken } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    description: '',
    genders: [],
    garment_types: '',
    customization_options: '',
    specific_elements: '',
    styling_details: '',
  });

  const [loading, setLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [genderError, setGenderError] = useState(false);

  useEffect(() => {
    const fetchCustomizations = async () => {
      try {
        const response = await axios.get(`https://vasstrinfo.com/api/get_customisations/`, {
          headers: {
            'Content-Type': 'application/json',
             Authorization: `Bearer ${authToken?.access}`,

          },
        });
        const data = response.data;
        if (data.success && data.data) {
          setFormData({
            description: data.data.description || '',
            genders: data.data.genders || [],
            garment_types: data.data.garment_types || '',
            customization_options: data.data.customization_options || '',
            specific_elements: data.data.specific_elements || '',
            styling_details: data.data.styling_details || '',
          });
        }
      } catch (error) {
        console.error('Failed to fetch customizations:', error);
      }
    };

    fetchCustomizations();
  }, []);

  const handleCheckboxChange = (e) => {
    const { value, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      genders: checked
        ? [...prev.genders, value]
        : prev.genders.filter((gender) => gender !== value),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitSuccess(null);
    setSubmitError(null);

    if (formData.genders.length === 0) {
      setGenderError(true);
      return;
    }
    setGenderError(false);
    setLoading(true);

    try {
      const response = await axios.post(
        `https://vasstrinfo.com/api/customize_options/`,
        formData,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken?.access}`,

          },
        }
      );

      if (response.data.success) {
        setSubmitSuccess('Customization saved successfully!');
      } else {
        setSubmitError(response.data.error || 'Failed to save customization.');
      }
    } catch (error) {
      setSubmitError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Base
      Content={
        <div className="font-lora font-normal max-w-4xl mx-auto p-5">
          <div className="mb-8 text-center">
            <h3 className="font-semibold text-3xl mb-1 text-orange-600">Account</h3>
            <p className="text-xl text-orange-500">{user?.username || 'Username'}</p>
          </div>

          <div className="p-6 border border-gray-300 rounded-lg shadow-xl">
            <h2 className="font-semibold font-lora text-center text-3xl mb-6 text-orange-600">
              Edit "About Me"
              <p className='text-sm text-black'>This information will be visible in your designer profile at {user.username}.Vasstr.com</p>
            </h2>

            <form onSubmit={handleSubmit} id="customizationForm" noValidate>
              <div className="mb-6">
                <label htmlFor="description" className="block text-lg font-medium mb-2">
                  Tell us about yourself <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  id="description"
                  maxLength="1000"
                  rows={4}
                  className="w-full p-3 border border-gray-300 rounded bg-[#fffaf3] text-lg resize-y focus:outline-none focus:ring-2 focus:ring-orange-400"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-lg font-medium mb-2">
                  What genders do you stitch? <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-8">
                  {['men', 'women', 'kids'].map((gender) => (
                    <label key={gender} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="genders"
                        value={gender}
                        checked={formData.genders.includes(gender)}
                        onChange={handleCheckboxChange}
                        className="w-5 h-5 cursor-pointer"
                      />
                      <span className="capitalize text-lg">{gender}</span>
                    </label>
                  ))}
                </div>
                {genderError && (
                  <p className="mt-2 text-sm text-red-600 font-semibold">
                    Please select at least one gender.
                  </p>
                )}
              </div>

              {[
                {
                  id: 'garment_types',
                  label: 'What types of garments do you typically design?',
                  maxLength: 200,
                  required: true,
                },
                {
                  id: 'customization_options',
                  label: 'What customization options do you provide for your designs?',
                  maxLength: 200,
                  required: true,
                },
                {
                  id: 'specific_elements',
                  label:
                    "Will you be able to incorporate specific elements or themes based on a client's request?",
                  maxLength: 200,
                  required: true,
                },
                {
                  id: 'styling_details',
                  label: 'Please provide details of the styling services:',
                  maxLength: 200,
                  required: false,
                },
              ].map(({ id, label, maxLength, required }) => (
                <div className="mb-6" key={id}>
                  <label htmlFor={id} className="block text-lg font-medium mb-2">
                    {label} {required && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    id={id}
                    name={id}
                    maxLength={maxLength}
                    rows={3}
                    className="w-full p-3 border border-gray-300 rounded bg-[#fffaf3] text-lg resize-y focus:outline-none focus:ring-2 focus:ring-orange-400"
                    value={formData[id]}
                    onChange={handleInputChange}
                    required={required}
                  />
                </div>
              ))}

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 text-white text-lg rounded shadow transition
                  ${loading ? 'bg-orange-300 cursor-not-allowed' : 'bg-[#d25614] hover:bg-[#e67e0e]'}`}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>

              {submitSuccess && (
                <p className="mt-4 text-green-600 font-semibold text-center">{submitSuccess}</p>
              )}
              {submitError && (
                <p className="mt-4 text-red-600 font-semibold text-center">{submitError}</p>
              )}
            </form>
          </div>

          <div className="h-16" />
        </div>
      }
    />
  );
};

export default AboutMe;
